import { useCombinedCampaignStatuses } from "@/api/app-configuration.api";
import {
  getCampaignLeadCostQueryOptions,
  getCampaignQueryOptions,
  getCampaignRunMaxLeadsQueryOptions,
  getCampaignRunsQueryOptions,
  useDeleteCampaign,
  useUpdateCampaignMutation,
} from "@/api/campaign.api";
import { creditsQueryOptions } from "@/api/credits.api";
import { createLeadsQueryOptions } from "@/api/leads.api";
import { createUsersQueryOptions } from "@/api/users.api";
import { Menu as CampaignMenu } from "@/components/campaign/configuration/Menu";
import { CopyCampaignModal } from "@/components/campaign/CopyCampaignModal";
import { createPlanningSteps } from "@/components/campaign/setup/planning/steps";
import { AlertModal } from "@/components/generic/AlertModal";
import { Button } from "@/components/generic/Button";
import { Menu, MenuItem } from "@/components/generic/Menu";
import { Separator } from "@/components/generic/Separator";
import {
  composeTailwindRenderProps,
  focusRing,
} from "@/components/generic/utils";
import { MainWindow } from "@/components/layout/MainWindow";
import { PageHeader } from "@/components/layout/PageHeader";
import { Sidebar } from "@/components/layout/Sidebar";
import { TrialCTAModal } from "@/components/trial/TrialCTAModal";
import {
  trialCTAModalPropsAtom,
  trialExpiredText,
  trialText,
  useTrialPeriod,
} from "@/hooks/use-trial-period";
import {
  PageHeaderProvider,
  usePageHeader,
} from "@/providers/PageHeaderProvider";
import { getCampaignConfigurationCounts } from "@/queries/configurations";
import { getAvailableIntegrationsQueryOptions } from "@/queries/integrations";
import { isAdmin, useIsAdmin } from "@/services/auth.service";
import { getCampaignUpdateCreditStats } from "@/services/credits.service";
import {
  NewCampaignState,
  NewCampaignStateDelivery,
  NewCampaignStatePlanning,
} from "@/services/new-campaign.service";
import { useExpectedCampaignRunFallback } from "@/services/push-message-fallback.service";
import { formatScheduleLabel } from "@/services/schedule.service";
import {
  Campaign,
  CampaignUpdate,
  planningTypes,
} from "@/types/campaign";
import { entriesToRecord } from "@/utils/array-utils";
import { localToUTCDate, UTCToLocalDate } from "@/utils/dates";
import { store } from "@/utils/jotai";
import { formatNumber } from "@/utils/number-utils";
import { formatPeriodDescription } from "@/utils/sidebar";
import { createStepper } from "@/utils/stepper";
import { pluralize } from "@/utils/string-utils";
import { parseDate } from "@internationalized/date";
import {
  queryOptions,
  useMutation,
  useQuery,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  notFound,
  Outlet,
  rootRouteId,
  useNavigate,
} from "@tanstack/react-router";
import { atom, useAtom } from "jotai";
import { HTTPError } from "ky";
import {
  ArchiveIcon,
  EllipsisVertical,
  Loader2,
  TrashIcon,
} from "lucide-react";
import {
  Fragment,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { MenuTrigger, Button as RACButton } from "react-aria-components";
import { toast } from "sonner";
import z from "zod";
import { newlyCreatedCampaignIdAtom } from "../-state";
import { Route as StartCampaignBuyCreditsRoute } from "./(edit)/start-campaign/buy-credits";
import { Route as StartCampaignRoute } from "./(edit)/start-campaign/route";

const campaignTypeNameMap = entriesToRecord<string, string | undefined>(
  planningTypes.map((dM) => [dM.type, dM.name]),
);

const editCampaignAtom = atom<Campaign>();

export const Route = createFileRoute("/_dashboard/campaigns/$id")({
  component: RouteComponent,
  params: z.object({
    id: z.coerce.number().describe("ID of the campaign to edit."),
  }),
  pendingComponent: () => (
    <MainWindow className="flex items-center justify-center">
      <div className="flex flex-col items-center justify-center gap-4">
        <Loader2 size={32} className="animate-spin" />
        <div className="animate-pulse">Indlæser kampagne...</div>
      </div>
    </MainWindow>
  ),
  beforeLoad: async ({ context, params, location }) => {
    const campaignQueryOptions = getCampaignQueryOptions(params.id);
    const campaignRunsQueryOptions = getCampaignRunsQueryOptions(params.id);
    const allLeadsQueryOptions = createLeadsQueryOptions({
      campaignId: [params.id],
      pageSize: 1,
    });

    const usersQueryOptions = createUsersQueryOptions({ nameFilter: "" });
    const availableIntegrationsQueryOptions =
      getAvailableIntegrationsQueryOptions();

    context.queryClient.prefetchQuery(availableIntegrationsQueryOptions);

    const [[campaign, countsQueryOptions, campaignRunMaxLeadsQueryOptions]] =
      await Promise.all([
        context.queryClient
          .ensureQueryData(campaignQueryOptions)
          .then(async (campaign) => {
            const countsQueryOptions = getCampaignConfigurationCounts(campaign);

            const maxLeadsEnabled =
              campaign.type === "Scheduled" &&
              isAdmin(context.user) &&
              !context.trialPeriod;

            // Pass the fresh counts to /run/max, as the backend's cached estimate is often outdated.
            const remainingLeads = maxLeadsEnabled
              ? (
                  await context.queryClient.ensureQueryData(countsQueryOptions)
                ).reduce((sum, { count }) => sum + count, 0)
              : undefined;

            const campaignRunMaxLeadsQueryOptions = queryOptions({
              ...getCampaignRunMaxLeadsQueryOptions(params.id, {
                deliverContacts: campaign.deliverContacts ?? false,
                deliverContactsRequirePhone:
                  campaign.deliverContactsRequirePhone ?? false,
                deliverLiveNumber: campaign.deliverLiveNumber ?? false,
                completionEmail: campaign.completionEmail ?? null,
                remainingLeads,
              }),
              enabled: maxLeadsEnabled,
            });

            if (campaignRunMaxLeadsQueryOptions.enabled)
              await context.queryClient.ensureQueryData(
                campaignRunMaxLeadsQueryOptions,
              );

            return [
              campaign,
              countsQueryOptions,
              campaignRunMaxLeadsQueryOptions,
            ] as const;
          }),
      context.queryClient.ensureQueryData(campaignRunsQueryOptions),
      context.queryClient.ensureInfiniteQueryData(allLeadsQueryOptions),
      context.queryClient.ensureInfiniteQueryData(usersQueryOptions),
    ]);

    const useCampaignQuery = () => useSuspenseQuery(campaignQueryOptions);
    const useCampaignRunsQuery = () =>
      useSuspenseQuery(campaignRunsQueryOptions);
    const useAllLeadsQuery = () =>
      useSuspenseInfiniteQuery(allLeadsQueryOptions, context.queryClient);
    const useCampaignRunMaxLeadsQuery = () =>
      useSuspenseQuery(campaignRunMaxLeadsQueryOptions);

    store.set(editCampaignAtom, (prev) =>
      prev?.id === campaign.id ? prev : campaign,
    );

    const useEditCampaign = () => {
      const navigate = useNavigate();

      const campaignQuery = useCampaignQuery();
      const { data: campaign } = campaignQuery;

      const isSameCampaign = (c1: Campaign, c2?: Campaign): c2 is Campaign =>
        c1.id === c2?.id;

      const [_editCampaign, _setEditCampaign] = useAtom(editCampaignAtom);

      const editCampaign = useMemo(
        () =>
          isSameCampaign(campaign, _editCampaign) ? _editCampaign : campaign,
        [_editCampaign, campaign],
      );

      const setEditCampaign = useCallback(
        (action: SetStateAction<Campaign>) =>
          _setEditCampaign((prev) =>
            typeof action === "function"
              ? action(isSameCampaign(campaign, prev) ? prev : campaign)
              : action,
          ),
        [_setEditCampaign, campaign],
      );

      const isDirty = useMemo(
        () => editCampaign !== campaign,
        [campaign, editCampaign],
      );

      const resetCampaign = useCallback(
        () => setEditCampaign(campaign),
        [campaign, setEditCampaign],
      );

      const updateCampaignMutation = useUpdateCampaignMutation();

      const updateCampaign = useCallback(
        async (payload: CampaignUpdate) => {
          const updatePromise = updateCampaignMutation.mutateAsync({
            id: editCampaign.id,
            ...payload,
          });

          toast.promise(updatePromise, {
            loading: "Opdaterer kampagne...",
            success: "Kampagnen er opdateret.",
            error: "Kampagnen kunne ikke opdateres.",
          });

          const updatedCampaign = await updatePromise;

          setEditCampaign(updatedCampaign);
        },
        [editCampaign.id, setEditCampaign, updateCampaignMutation],
      );

      const startCampaignMutation = useMutation({
        mutationKey: ["startCampaign", campaign.id],
        mutationFn: async ({
          ignoreCredits = false,
        }: {
          ignoreCredits?: boolean;
        }) => {
          if (campaign.status === "Active" || campaign.status === "Archived")
            return;

          const chosenStartDate = campaign.chosenStartDate
            ? UTCToLocalDate(new Date(campaign.chosenStartDate))
            : null;

          const newStartDate =
            chosenStartDate && chosenStartDate.getTime() > new Date().getTime()
              ? null
              : chosenStartDate;

          if (!ignoreCredits) {
            const [credits, campaignLeadCost, updatedCampaignLeadCost] =
              await Promise.all([
                context.queryClient.ensureQueryData(creditsQueryOptions),
                context.queryClient.ensureQueryData(
                  getCampaignLeadCostQueryOptions(campaign),
                ),
                context.queryClient.ensureQueryData(
                  getCampaignLeadCostQueryOptions({
                    ...campaign,
                    status: "Active",
                    chosenStartDate: newStartDate
                      ? localToUTCDate(newStartDate).toISOString()
                      : null,
                    chosenEndDate: null,
                  }),
                ),
              ]);

            const {
              updatedCreditsNeededRestOfPeriod,
              updatedCreditsNeededNextPeriod,
            } = getCampaignUpdateCreditStats({
              credits,
              campaignLeadCost,
              updatedCampaignLeadCost,
            });

            const neededCredits = Math.max(
              updatedCreditsNeededRestOfPeriod,
              updatedCreditsNeededNextPeriod,
            );

            if (neededCredits) {
              await navigate({
                from: Route.fullPath,
                to: StartCampaignBuyCreditsRoute.fullPath,
                state: {
                  routeStates: {
                    [StartCampaignRoute.id]: {
                      returnTo: {
                        to: location.pathname,
                        search: location.search,
                      },
                    },
                    [StartCampaignBuyCreditsRoute.id]: {
                      neededCredits,
                    },
                  },
                },
              });
              return;
            }
          }

          await updateCampaign({
            status: "Active",
            chosenStartDate: newStartDate
              ? localToUTCDate(newStartDate).toISOString()
              : null,
            chosenEndDate: null,
          });
          toast.success("Kampagnen er startet.");
        },
        onError: () => {
          toast.error("Kampagnen kunne ikke startes.");
        },
      });

      return {
        campaign,
        editCampaign,
        setEditCampaign,
        isDirty,
        resetCampaign,
        updateCampaignMutation,
        updateCampaign,
        startCampaignMutation,
      };
    };

    function createStepperContext(campaign: Campaign): Pick<
      NewCampaignState,
      | "isSetup"
      | "configurations"
      | "isTrialPeriod"
      | "canSendContactsToIntegration"
      | "canSendLiveNumbersToIntegration"
    > & {
      planning: NewCampaignStatePlanning;
      delivery: NewCampaignStateDelivery;
    } {
      const parsePeriodDate = (date: string | null | undefined) =>
        date ? parseDate(date.split("T")[0]) : null;

      let planning: NewCampaignStatePlanning;

      const planningBase = {
        deliverContacts: campaign.deliverContacts,
        deliverContactsRequirePhone: campaign.deliverContactsRequirePhone,
        deliverLiveNumber: campaign.deliverLiveNumber,
      };

      switch (campaign.type) {
        case "OnDemand":
          planning = {
            ...planningBase,
            type: "OnDemand",
            leadAmount: null,
          };
          break;

        case "Scheduled":
          planning = {
            ...planningBase,
            type: "Scheduled",
            schedules: campaign.schedules,
            chosenStartDate: parsePeriodDate(campaign.chosenStartDate),
            chosenEndDate: parsePeriodDate(campaign.chosenEndDate),
          };
          break;

        case "Triggered":
          planning = {
            ...planningBase,
            type: "Triggered",
            triggers: campaign.triggers,
            maxLeadsPerWeek: campaign.maxLeadsPerWeek,
            chosenStartDate: parsePeriodDate(campaign.chosenStartDate),
            chosenEndDate: parsePeriodDate(campaign.chosenEndDate),
          };
          break;

        default:
          throw Error(`Unsupported campaign type: ${campaign.type}`);
      }

      return {
        isSetup: false,
        isTrialPeriod: !!context.trialPeriod,
        canSendContactsToIntegration:
          context.appConfiguration.features.canSendContactsToIntegration,
        canSendLiveNumbersToIntegration:
          context.appConfiguration.features.canSendLiveNumbersToIntegration,
        configurations: campaign.configurations,
        planning: planning,
        delivery: campaign.exportSystem
          ? {
              type: "Integration",
              exportSystem: campaign.exportSystem,
              exportMode: campaign.exportMode,
              exportInput: campaign.exportInput,
            }
          : campaign.completionEmail
            ? {
                type: "Excel",
                completionEmail: campaign.completionEmail,
              }
            : {
                type: "LassoSales",
              },
      };
    }

    const stepperContext = createStepperContext(campaign);

    const planningStepper = createStepper({
      context: stepperContext,
      steps: createPlanningSteps({
        triggers: {
          id: "planning/triggers",
          to: "/campaigns/$id/planning/triggers",
        },
        "lead-amount-and-period": {
          id: "planning/lead-amount-and-period",
          to: "/campaigns/$id/planning/lead-amount-and-period",
        },
        "sales-strategy-and-prioritization": {
          id: "planning/sales-strategy-and-prioritization",
          to: "/campaigns/$id/planning/sales-strategy-and-prioritization",
        },
        "contact-information": {
          id: "planning/contact-information",
          to: "/campaigns/$id/planning/contact-information",
        },
      }),
    });

    const useTrialCTAModal = () => useAtom(trialCTAModalPropsAtom);

    return {
      location,
      campaign,
      campaignQueryOptions,
      useCampaignQuery,
      campaignRunsQueryOptions,
      useCampaignRunsQuery,
      allLeadsQueryOptions,
      useAllLeadsQuery,
      countsQueryOptions,
      campaignRunMaxLeadsQueryOptions,
      useCampaignRunMaxLeadsQuery,
      usersQueryOptions,
      availableIntegrationsQueryOptions,
      editCampaignAtom,
      useEditCampaign,
      stepperContext,
      createStepperContext,
      useStepperContext: (campaign: Campaign) =>
        useMemo(() => createStepperContext(campaign), [campaign]),
      planningStepper,
      useTrialCTAModal,
    };
  },
  loader: async ({ context }) => {
    const campaign = await context.queryClient.ensureQueryData(
      context.campaignQueryOptions,
    );

    await context.queryClient.prefetchQuery(context.countsQueryOptions);

    if (campaign.exportSystem) {
      await context.queryClient.ensureQueryData(
        context.availableIntegrationsQueryOptions,
      );
    }

    return {
      meta: {
        title: campaign.name || "Kampagne",
      },
    };
  },
  onError: (error) => {
    if (error instanceof HTTPError && error.response.status === 404) {
      // ? Setting routeId to rootRouteId ensures the notFound component is rendered as high up in the route tree as possible
      throw notFound({
        data: { resource: "kampagnen" },
        throw: true,
        routeId: rootRouteId,
      });
    }
  },
  onLeave: () => {
    store.set(editCampaignAtom, undefined);
  },
});

function RouteComponent() {
  const {
    queryClient,
    campaignQueryOptions,
    usersQueryOptions,
    availableIntegrationsQueryOptions,
    ...context
  } = Route.useRouteContext();
  const navigate = Route.useNavigate();

  const isAdmin = useIsAdmin();

  const trialPeriod = useTrialPeriod();
  const [trialCTAModalProps, setTrialCTAModalProps] =
    context.useTrialCTAModal();

  const { campaign, setEditCampaign } = context.useEditCampaign();

  // Built from the saved campaign, so the count follows it when the campaign is saved.
  // Keeps showing the previous count while the new one loads.
  const { data: counts } = useQuery(
    getCampaignConfigurationCounts(campaign),
    queryClient,
  );

  const targetGroupCount = useMemo(() => {
    return counts?.reduce((acc, count) => {
      acc += count.count;
      return acc;
    }, 0);
  }, [counts]);

  // Reset edit campaign when campaign query data changes.
  useEffect(() => {
    setEditCampaign(campaign);
  }, [campaign, setEditCampaign]);

  const allLeadsQuery = context.useAllLeadsQuery();

  const totalLeadsCount = useMemo(
    () => allLeadsQuery.data.pages.at(-1)?.resultsFound,
    [allLeadsQuery.data.pages],
  );

  const usersQuery = useSuspenseInfiniteQuery(usersQueryOptions);

  const usersById = useMemo(
    () =>
      new Map(
        usersQuery.data.pages.flatMap((page) =>
          page.results.map((user) => [user.id, user]),
        ),
      ),
    [usersQuery.data],
  );

  const { data: availableIntegrations } = useQuery({
    ...availableIntegrationsQueryOptions,
    enabled: !!campaign.exportSystem,
  });

  const availableIntegrationsMap = useMemo(
    () =>
      new Map(
        availableIntegrations?.map((integration) => [
          integration.uniqueName.toLowerCase(),
          integration,
        ]),
      ),
    [availableIntegrations],
  );

  const stepperContext = context.useStepperContext(campaign);

  const planningStepper = context.planningStepper.useStepper({
    context: stepperContext,
  });

  const deliveryDescription = useMemo(() => {
    const { delivery } = stepperContext;
    switch (delivery.type) {
      case "LassoSales":
        return "Leads håndteres i Lasso Sales";
      case "Excel":
        return `Excel sendes til "${delivery.completionEmail}"`;
      case "Integration":
        return (
          <div className="flex flex-col">
            <span>
              Sendes til{" "}
              {availableIntegrationsMap.get(
                delivery.exportSystem?.toLowerCase() ?? "",
              )?.name || "Integration"}
            </span>
            {delivery.exportMode === "SalesManual" ? (
              <span>Kun kvalificerede leads</span>
            ) : null}
          </div>
        );
    }
  }, [availableIntegrationsMap, stepperContext]);

  const planningDescription = useMemo(() => {
    const { planning } = planningStepper.context;

    switch (campaign.type) {
      case "OnDemand":
        return campaignTypeNameMap[campaign.type] || campaign.type || "-";
      case "Scheduled":
        return (
          <div className="flex flex-col">
            <span>{formatScheduleLabel(planning.schedules ?? [])}</span>
            <span>
              {formatPeriodDescription(
                planning.chosenStartDate?.toString(),
                planning.chosenEndDate?.toString(),
                !planning.chosenEndDate
                  ? (campaign.estimatedEndDate ?? undefined)
                  : undefined,
              )}
            </span>
          </div>
        );
      case "Triggered":
        return `${formatNumber(planning.triggers?.length ?? 0)} ${pluralize(planning.triggers?.length ?? 0, "trigger", "triggers")} valg`;
    }
  }, [campaign.estimatedEndDate, campaign.type, planningStepper.context]);

  const detailsDescription = useMemo(
    () =>
      [
        campaign.name,
        campaign.responsibleId
          ? usersById.get(campaign.responsibleId)?.name
          : undefined,
      ]
        .filter(Boolean)
        .map((line, index) => <div key={index}>{line}</div>),
    [campaign.name, campaign.responsibleId, usersById],
  );

  const [showCopyCampaignModal, setShowCopyCampaignModal] = useState(false);

  const canEndCampaign = useMemo(
    () => isAdmin && !trialPeriod && campaign.status === "Active",
    [isAdmin, trialPeriod, campaign.status],
  );
  const canStartCampaign = useMemo(
    () => isAdmin && !trialPeriod && campaign.status !== "Archived",
    [isAdmin, trialPeriod, campaign.status],
  );

  const { getCombinedCampaignStatus } = useCombinedCampaignStatuses();

  const combinedStatus = useMemo(
    () => getCombinedCampaignStatus(campaign.status, campaign.state),
    [campaign.state, campaign.status, getCombinedCampaignStatus],
  );

  const [newlyCreatedCampaignId] = useAtom(newlyCreatedCampaignIdAtom);
  const isNewlyCreatedCampaign = useMemo(
    () => campaign.id === newlyCreatedCampaignId,
    [campaign.id, newlyCreatedCampaignId],
  );

  useExpectedCampaignRunFallback(campaign.id);

  return (
    <>
      <PageHeaderProvider>
        {isAdmin && (
          <Sidebar
            className="[&>div>div]:pb-0 [&>div>div]:xl:pb-0"
            direction="right"
          >
            <div className="flex size-full flex-col">
              <PageHeader className="px-8 xl:px-8" sticky={false}>
                <div className="flex w-full items-center justify-between gap-2">
                  <span className="text-sm/4 font-medium">
                    {combinedStatus?.longName ?? "Ukendt"}
                  </span>

                  <CampaignDotsMenu
                    campaign={campaign}
                    openCopyModal={() => setShowCopyCampaignModal(true)}
                  />
                </div>
              </PageHeader>

              <div className="p-4 xl:py-8">
                <CampaignMenu
                  items={[
                    {
                      description:
                        totalLeadsCount !== undefined
                          ? `${formatNumber(totalLeadsCount)} leveret`
                          : "-",
                      name: "Leads",
                      to: "/campaigns/$id",
                      exact: true,
                    },
                    {
                      description:
                        targetGroupCount !== null &&
                        targetGroupCount !== undefined
                          ? `${formatNumber(targetGroupCount)} leads tilbage`
                          : "-",
                      name: "Målgruppe",
                      locked: !!trialPeriod?.isExpired,
                      onLockedClick: () =>
                        setTrialCTAModalProps({
                          title: trialPeriod?.isExpired
                            ? trialExpiredText
                            : trialText,
                        }),
                      to: "/campaigns/$id/define",
                    },
                    {
                      name: "Levering",
                      description: deliveryDescription,
                      locked: !!trialPeriod,
                      onLockedClick: () =>
                        setTrialCTAModalProps({
                          title: trialPeriod?.isExpired
                            ? trialExpiredText
                            : trialText,
                        }),
                      to: "/campaigns/$id/delivery",
                    },
                    {
                      description: planningDescription,
                      name: "Planlægning",
                      to: "/campaigns/$id/planning",
                      locked: !!trialPeriod?.isExpired,
                      onLockedClick: () =>
                        setTrialCTAModalProps({
                          title: trialPeriod?.isExpired
                            ? trialExpiredText
                            : trialText,
                        }),
                      submenu: planningStepper.visibleSteps.map((step) => ({
                        name: step.name,
                        to: step.to,
                        locked: step.isLocked,
                        onLockedClick: () =>
                          setTrialCTAModalProps({
                            title: trialPeriod?.isExpired
                              ? trialExpiredText
                              : trialText,
                          }),
                      })),
                    },
                    {
                      name: "Detaljer",
                      description: detailsDescription,
                      locked: !!trialPeriod?.isExpired,
                      onLockedClick: () =>
                        setTrialCTAModalProps({
                          title: trialPeriod?.isExpired
                            ? trialExpiredText
                            : trialText,
                        }),
                      to: "/campaigns/$id/details",
                    },
                  ]}
                />
              </div>

              {campaign.type !== "OnDemand" && (
                <div className="mt-auto flex shrink-0 grow flex-col justify-end p-4 pt-0 xl:p-8 xl:pt-0">
                  {canEndCampaign ? (
                    <EndCampaignButton />
                  ) : canStartCampaign ? (
                    <StartCampaignButton />
                  ) : null}
                </div>
              )}
            </div>
          </Sidebar>
        )}

        <MainWindow
          className="relative flex flex-col"
          data-lso-newly-created-campaign={isNewlyCreatedCampaign || undefined}
        >
          <CampaignPageHeader />

          <Outlet />
        </MainWindow>
      </PageHeaderProvider>

      <CopyCampaignModal
        isOpen={showCopyCampaignModal}
        onOpenChange={setShowCopyCampaignModal}
        campaign={campaign}
        onCopyCampaign={(copiedCampaign) => {
          navigate({
            to: "/campaigns/$id",
            params: { id: copiedCampaign.id },
          });
        }}
      />
      <TrialCTAModal
        isOpen={!!trialCTAModalProps}
        onOpenChange={(open) =>
          setTrialCTAModalProps(open ? trialCTAModalProps : undefined)
        }
        title=""
        {...trialCTAModalProps}
      />
    </>
  );
}

/** PageHeader that syncs with PageHeaderProvider context */
function CampaignPageHeader() {
  const {
    title,
    backLinkOptions,
    onBackButtonClick,
    actions,
    breadcrumbs,
    hidden,
  } = usePageHeader();

  if (hidden) {
    return null;
  }

  return (
    <PageHeader
      title={title}
      backLinkOptions={backLinkOptions}
      onBackButtonClick={onBackButtonClick}
      actions={actions}
      breadcrumbs={breadcrumbs}
      sticky={true}
    />
  );
}

function EndCampaignButton() {
  const context = Route.useRouteContext();
  const { data: campaign } = useSuspenseQuery(context.campaignQueryOptions);
  const { isPending, mutateAsync } = useUpdateCampaignMutation();

  const endCampaign = useCallback(async () => {
    try {
      await mutateAsync({ ...campaign, status: "Ended" });
      toast.success("Kampagnen er stoppet.");
    } catch {
      toast.error("Kampagnen kunne ikke stoppes.");
    }
  }, [mutateAsync, campaign]);

  return (
    <Button
      size="lg"
      intent="secondary"
      isPending={isPending}
      onPress={endCampaign}
    >
      Stop kampagne
    </Button>
  );
}

function StartCampaignButton() {
  const context = Route.useRouteContext();
  const { startCampaignMutation } = context.useEditCampaign();

  return (
    <Button
      size="lg"
      isPending={startCampaignMutation.isPending}
      onPress={() => startCampaignMutation.mutate({ ignoreCredits: false })}
    >
      Start kampagne
    </Button>
  );
}

function CampaignDotsMenu({
  campaign,
  openCopyModal,
}: {
  campaign: Campaign;
  openCopyModal: () => void;
}) {
  const context = Route.useRouteContext();

  const { id: campaignId, status: currentStatus } = campaign;

  const { updateCampaign } = context.useEditCampaign();

  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const deleteCampaignMutation = useDeleteCampaign();

  const trialPeriod = useTrialPeriod();

  const archiveCampaign = useCallback(async () => {
    const promise = updateCampaign({
      id: campaignId,
      status: "Archived",
    });

    toast.promise(promise, {
      loading: "Arkiverer kampagne...",
      success: () => "Kampagnen er arkiveret.",
      error: () => "Kampagnen kunne ikke arkiveres.",
    });

    await promise;
    setShowArchiveModal(false);
  }, [campaignId, updateCampaign]);

  const deleteCampaign = useCallback(async () => {
    const promise = deleteCampaignMutation.mutateAsync({
      id: campaignId ?? NaN, // Should never be undefined here
    });

    toast.promise(promise, {
      loading: "Sletter kampagne...",
      success: () => "Kampagnen er slettet.",
      error: () => "Kampagnen kunne ikke slettes.",
    });

    await promise;
    setShowDeleteModal(false);
  }, [campaignId, deleteCampaignMutation]);

  const menuItemGroups = useMemo(
    () =>
      [
        ...(campaignId !== undefined && !trialPeriod
          ? [
              [
                !context.trialPeriod && {
                  key: "duplicate",
                  label: "Dupliker kampagne",
                  action: () => {
                    openCopyModal();
                  },
                },
                currentStatus !== "Archived" && {
                  key: "archive",
                  label: "Arkiver kampagne",
                  action: () => setShowArchiveModal(true),
                },
              ],
              [
                {
                  key: "delete",
                  label: "Slet kampagne",
                  action: () => setShowDeleteModal(true),
                },
              ],
            ]
          : []),
      ]
        .map((group) => group.filter((item) => !!item))
        .filter((group) => group.length),
    [
      campaignId,
      trialPeriod,
      context.trialPeriod,
      currentStatus,
      openCopyModal,
    ],
  );

  if (!menuItemGroups.length) return null;

  return (
    <>
      <MenuTrigger>
        <RACButton
          className={composeTailwindRenderProps(
            focusRing,
            "hover:text-theme-secondary -m-2 flex h-auto cursor-pointer rounded-full p-2",
          )}
        >
          <EllipsisVertical className="size-5" />
        </RACButton>

        <Menu placement="bottom end">
          {menuItemGroups.map((menuItems, index) => (
            <Fragment key={index}>
              {!!index && <Separator className="my-3" />}

              {menuItems.map((item) => (
                <MenuItem key={item.key} onAction={item.action}>
                  {item.label}
                </MenuItem>
              ))}
            </Fragment>
          ))}
        </Menu>
      </MenuTrigger>

      {showArchiveModal && (
        <AlertModal
          title="Arkiver kampagne?"
          description="Kampagnen vil ikke levere flere leads, og vil blive skjult fra overblikket. Leads, modtaget på denne kampagne, vil fortsat være synlige. Denne handling kan ikke fortrydes."
          onConfirm={archiveCampaign}
          onClose={() => setShowArchiveModal(false)}
          icon={<ArchiveIcon className="text-theme-active" />}
        />
      )}

      {showDeleteModal && (
        <AlertModal
          title="Slet kampagne?"
          description={
            <>
              Modtagne leads på denne kampagne vil også blive slettet. Dette kan
              ikke fortrydes. Hvis du ønsker at beholde dine leads, skal du i
              stedet arkivere kampagnen.
              <br />
              <br />
              Ønsker du stadig at slette kampagnen?
            </>
          }
          onConfirm={deleteCampaign}
          onClose={() => setShowDeleteModal(false)}
          icon={<TrashIcon className="text-theme-active" />}
        />
      )}
    </>
  );
}
